/**
 * Admin settings page for authentication provider configuration.
 *
 * Allows administrators to enable/disable and configure Auth0 and Authentik providers.
 */

import React, { useState, useEffect } from 'react';
import { useApiService } from '../../services/apiService';

interface Auth0Config {
  enabled: boolean;
  domain?: string;
  audience?: string;
  management_client_id?: string;
}

interface AuthentikConfig {
  enabled: boolean;
  url?: string;
  client_id?: string;
  app_slug?: string;
}

interface AuthProvidersConfig {
  auth0: Auth0Config;
  authentik: AuthentikConfig;
  active_providers: string[];
  updated_at?: string;
}

interface TestResult {
  provider: string;
  success: boolean;
  message: string;
  details?: any;
  response_time_ms?: number;
}

/**
 * Provider configuration card component.
 */
function ProviderCard({
  title,
  description,
  enabled,
  onToggle,
  children,
  testResult,
  onTest,
  isTestLoading,
}: {
  title: string;
  description: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  children: React.ReactNode;
  testResult?: TestResult | null;
  onTest: () => void;
  isTestLoading: boolean;
}) {
  return (
    <div className={`card p-6 ${enabled ? 'ring-2 ring-primary-500' : ''}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {description}
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onToggle(e.target.checked)}
            className="sr-only peer"
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4
                         peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full
                         peer dark:bg-gray-700 peer-checked:after:translate-x-full
                         peer-checked:after:border-white after:content-[''] after:absolute
                         after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300
                         after:border after:rounded-full after:h-5 after:w-5 after:transition-all
                         dark:border-gray-600 peer-checked:bg-primary-600"></div>
          <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
            {enabled ? 'Enabled' : 'Disabled'}
          </span>
        </label>
      </div>

      <div className={`space-y-4 ${enabled ? '' : 'opacity-50 pointer-events-none'}`}>
        {children}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <button
            onClick={onTest}
            disabled={!enabled || isTestLoading}
            className="btn btn-secondary"
          >
            {isTestLoading ? (
              <>
                <span className="animate-spin mr-2">&#9696;</span>
                Testing...
              </>
            ) : (
              'Test Connection'
            )}
          </button>

          {testResult && (
            <div className={`flex items-center gap-2 ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
              {testResult.success ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
              <span className="text-sm">{testResult.message}</span>
              {testResult.response_time_ms && (
                <span className="text-xs text-gray-500">({testResult.response_time_ms.toFixed(0)}ms)</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Text input field component.
 */
function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  helpText,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  helpText?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                  bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100
                  focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
      />
      {helpText && (
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{helpText}</p>
      )}
    </div>
  );
}

/**
 * Settings page component.
 */
export default function SettingsPage() {
  const apiService = useApiService();
  const [config, setConfig] = useState<AuthProvidersConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [auth0TestResult, setAuth0TestResult] = useState<TestResult | null>(null);
  const [authentikTestResult, setAuthentikTestResult] = useState<TestResult | null>(null);
  const [isTestingAuth0, setIsTestingAuth0] = useState(false);
  const [isTestingAuthentik, setIsTestingAuthentik] = useState(false);

  // Local form state
  const [auth0Config, setAuth0Config] = useState<Auth0Config>({
    enabled: true,
    domain: '',
    audience: '',
    management_client_id: '',
  });
  const [authentikConfig, setAuthentikConfig] = useState<AuthentikConfig>({
    enabled: false,
    url: '',
    client_id: '',
    app_slug: '',
  });

  // Fetch current configuration
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await apiService.getAuthProviders();
        setConfig(response.data);
        setAuth0Config({
          enabled: response.data.auth0.enabled,
          domain: response.data.auth0.domain || '',
          audience: response.data.auth0.audience || '',
          management_client_id: response.data.auth0.management_client_id || '',
        });
        setAuthentikConfig({
          enabled: response.data.authentik.enabled,
          url: response.data.authentik.url || '',
          client_id: response.data.authentik.client_id || '',
          app_slug: response.data.authentik.app_slug || '',
        });
      } catch (err: any) {
        setError(err.message || 'Failed to load settings');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfig();
  }, [apiService]);

  // Save configuration
  const handleSave = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // Validate at least one provider is enabled
      if (!auth0Config.enabled && !authentikConfig.enabled) {
        throw new Error('At least one authentication provider must be enabled');
      }

      await apiService.updateAuthProviders({
        auth0: auth0Config,
        authentik: authentikConfig,
      });

      setSuccessMessage('Settings saved successfully. Changes will take effect on next login.');
    } catch (err: any) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  // Test Auth0 connection
  const handleTestAuth0 = async () => {
    setIsTestingAuth0(true);
    setAuth0TestResult(null);

    try {
      const response = await apiService.testAuthProvider('auth0');
      setAuth0TestResult(response.data);
    } catch (err: any) {
      setAuth0TestResult({
        provider: 'auth0',
        success: false,
        message: err.message || 'Test failed',
      });
    } finally {
      setIsTestingAuth0(false);
    }
  };

  // Test Authentik connection
  const handleTestAuthentik = async () => {
    setIsTestingAuthentik(true);
    setAuthentikTestResult(null);

    try {
      const response = await apiService.testAuthProvider('authentik');
      setAuthentikTestResult(response.data);
    } catch (err: any) {
      setAuthentikTestResult({
        provider: 'authentik',
        success: false,
        message: err.message || 'Test failed',
      });
    } finally {
      setIsTestingAuthentik(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-h3 text-gray-900 dark:text-gray-100">
          Authentication Settings
        </h2>
        <p className="text-body-sm text-gray-600 dark:text-gray-400 mt-1">
          Configure authentication providers for the PING system.
          At least one provider must be enabled.
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2 text-green-800 dark:text-green-200">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>{successMessage}</span>
          </div>
        </div>
      )}

      {/* Auth0 Configuration */}
      <ProviderCard
        title="Auth0"
        description="Enterprise identity platform with extensive integrations"
        enabled={auth0Config.enabled}
        onToggle={(enabled) => setAuth0Config({ ...auth0Config, enabled })}
        testResult={auth0TestResult}
        onTest={handleTestAuth0}
        isTestLoading={isTestingAuth0}
      >
        <TextField
          label="Domain"
          value={auth0Config.domain || ''}
          onChange={(domain) => setAuth0Config({ ...auth0Config, domain })}
          placeholder="your-tenant.auth0.com"
          helpText="Your Auth0 tenant domain"
        />
        <TextField
          label="API Audience"
          value={auth0Config.audience || ''}
          onChange={(audience) => setAuth0Config({ ...auth0Config, audience })}
          placeholder="https://your-api.example.com"
          helpText="The API identifier configured in Auth0"
        />
        <TextField
          label="Management API Client ID"
          value={auth0Config.management_client_id || ''}
          onChange={(management_client_id) => setAuth0Config({ ...auth0Config, management_client_id })}
          placeholder="ABC123..."
          helpText="Client ID for Auth0 Management API access (for user/app sync)"
        />
      </ProviderCard>

      {/* Authentik Configuration */}
      <ProviderCard
        title="Authentik"
        description="Open-source identity provider with flexible authentication flows"
        enabled={authentikConfig.enabled}
        onToggle={(enabled) => setAuthentikConfig({ ...authentikConfig, enabled })}
        testResult={authentikTestResult}
        onTest={handleTestAuthentik}
        isTestLoading={isTestingAuthentik}
      >
        <TextField
          label="Authentik URL"
          value={authentikConfig.url || ''}
          onChange={(url) => setAuthentikConfig({ ...authentikConfig, url })}
          placeholder="https://auth.example.com"
          helpText="Base URL of your Authentik instance"
        />
        <TextField
          label="Client ID"
          value={authentikConfig.client_id || ''}
          onChange={(client_id) => setAuthentikConfig({ ...authentikConfig, client_id })}
          placeholder="ping-feedback"
          helpText="OIDC client ID from Authentik"
        />
        <TextField
          label="Application Slug"
          value={authentikConfig.app_slug || ''}
          onChange={(app_slug) => setAuthentikConfig({ ...authentikConfig, app_slug })}
          placeholder="ping-feedback"
          helpText="The application slug in Authentik (used for OIDC endpoints)"
        />
      </ProviderCard>

      {/* Active Providers Summary */}
      {config && (
        <div className="card p-4 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span className="font-medium">Active Providers:</span>
            {config.active_providers.length > 0 ? (
              <div className="flex gap-2">
                {config.active_providers.map((provider) => (
                  <span
                    key={provider}
                    className="px-2 py-0.5 rounded-full bg-primary-100 dark:bg-primary-900/30
                              text-primary-700 dark:text-primary-300 text-xs font-medium"
                  >
                    {provider}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-yellow-600 dark:text-yellow-400">None</span>
            )}
            {config.updated_at && (
              <span className="ml-auto text-xs">
                Last updated: {new Date(config.updated_at).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Save Button */}
      <div className="flex justify-end gap-4">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn btn-primary"
        >
          {isSaving ? (
            <>
              <span className="animate-spin mr-2">&#9696;</span>
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </button>
      </div>

      {/* Warning about changes */}
      <div className="text-sm text-gray-500 dark:text-gray-400 p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
        <strong>Note:</strong> Changes to authentication providers may require users to log out and log back in.
        Ensure you have tested the configuration before enabling a new provider.
      </div>
    </div>
  );
}
